import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useNavigate, useParams } from 'react-router';
import { useBackHref } from '@/hooks/useListLocation';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { Can } from '@/lib/permissions';
import {
  DevicePairingModal,
  type DevicePairingResult,
} from './DevicePairingModal';

const defaultGratuity = {
  enabled: false,
  minimumAmountCents: 1000,
  tipPercent1: 5,
  tipPercent2: 13,
  tipPercent3: 20,
  roundToHalf: true,
  allowCustomTip: true,
};

const DeviceDoc = graphql(/* GraphQL */ `
  query Device($id: ID!) {
    device(id: $id) {
      id
      name
      isActive
      allowCategorySwitch
      askLoyalty
      roleBindings {
        role {
          id
          name
          slug
        }
      }
      gratuitySettings {
        enabled
        minimumAmountCents
        tipPercent1
        tipPercent2
        tipPercent3
        roundToHalf
        allowCustomTip
      }
      category {
        id
        name
      }
      terminal {
        id
        name
      }
    }
    categories(includeInactive: true) {
      id
      name
    }
    terminals(includeInactive: true, pageSize: 500) {
      nodes {
        id
        name
      }
    }
  }
`);

const DeviceRolesCatalogDoc = graphql(/* GraphQL */ `
  query DeviceRolesCatalog {
    roles(actorKind: DEVICE) {
      id
      name
      slug
    }
  }
`);

const SetDeviceRolesDoc = graphql(/* GraphQL */ `
  mutation SetDeviceRoles($deviceId: UUID!, $roleId: UUID) {
    setDeviceRoles(deviceId: $deviceId, roleId: $roleId) {
      id
      roleBindings {
        role {
          id
          name
        }
      }
    }
  }
`);

const UpdateDeviceDoc = graphql(/* GraphQL */ `
  mutation UpdateDevice($id: ID!, $input: UpdateDeviceInput!) {
    updateDevice(id: $id, input: $input) {
      id
      name
      isActive
      allowCategorySwitch
      askLoyalty
      gratuitySettings {
        enabled
      }
    }
  }
`);

const PerformDeviceResetDoc = graphql(/* GraphQL */ `
  mutation PerformDeviceReset($id: ID!) {
    performDeviceAction(id: $id, action: RESET_DEVICE) {
      id
      name
      isActive
      askLoyalty
      gratuitySettings {
        enabled
      }
      category {
        id
        name
      }
      terminal {
        id
        name
      }
    }
  }
`);

const RegenerateDevicePairingCodeDoc = graphql(/* GraphQL */ `
  mutation RegenerateDevicePairingCode($deviceId: UUID!) {
    regenerateDevicePairingCode(deviceId: $deviceId) {
      pairingCode
      expiresAt
      device {
        id
        name
      }
    }
  }
`);

type GratuityFormState = typeof defaultGratuity;

export function DeviceEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backHref = useBackHref('/devices');
  const { data, loading, refetch } = useQuery(DeviceDoc, { variables: { id } });
  const { data: rolesCatalog } = useQuery(DeviceRolesCatalogDoc);
  const [updateDevice, { loading: saving }] = useMutation(UpdateDeviceDoc);
  const [setDeviceRoles] = useMutation(SetDeviceRolesDoc);
  const [performDeviceReset, { loading: resetting }] = useMutation(
    PerformDeviceResetDoc,
  );
  const [regeneratePairingCode, { loading: pairing }] = useMutation(
    RegenerateDevicePairingCodeDoc,
  );

  const device = data?.device;
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [terminalId, setTerminalId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [allowCategorySwitch, setAllowCategorySwitch] = useState(true);
  const [askLoyalty, setAskLoyalty] = useState(false);
  const [gratuity, setGratuity] = useState<GratuityFormState>(defaultGratuity);
  const [minimumEuros, setMinimumEuros] = useState('10.00');
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const [pairingResult, setPairingResult] = useState<DevicePairingResult | null>(
    null,
  );
  const [pairingModalOpen, setPairingModalOpen] = useState(false);

  if (device && device.id !== seededFor) {
    setSeededFor(device.id);
    setName(device.name);
    setCategoryId(device.category?.id ?? '');
    setTerminalId(device.terminal?.id ?? '');
    setRoleId(device.roleBindings?.[0]?.role.id ?? '');
    setIsActive(device.isActive);
    setAllowCategorySwitch(device.allowCategorySwitch);
    setAskLoyalty(device.askLoyalty);
    const gs = device.gratuitySettings;
    setGratuity({
      enabled: gs.enabled,
      minimumAmountCents: gs.minimumAmountCents,
      tipPercent1: gs.tipPercent1,
      tipPercent2: gs.tipPercent2,
      tipPercent3: gs.tipPercent3,
      roundToHalf: gs.roundToHalf,
      allowCustomTip: gs.allowCustomTip,
    });
    setMinimumEuros((gs.minimumAmountCents / 100).toFixed(2));
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const minimumAmountCents = Math.max(0, Math.round(parseFloat(minimumEuros || '0') * 100));
    await updateDevice({
      variables: {
        id,
        input: {
          name,
          categoryId: categoryId || null,
          terminalId: terminalId || null,
          isActive,
          allowCategorySwitch,
          askLoyalty,
          gratuitySettings: {
            enabled: gratuity.enabled,
            minimumAmountCents,
            tipPercent1: gratuity.tipPercent1,
            tipPercent2: gratuity.tipPercent2,
            tipPercent3: gratuity.tipPercent3,
            roundToHalf: gratuity.roundToHalf,
            allowCustomTip: gratuity.allowCustomTip,
          },
        },
      },
    });
    await setDeviceRoles({
      variables: { deviceId: id, roleId: roleId || null },
    });
    navigate(backHref);
  };

  const handleResetDevice = async () => {
    if (resetting) return;
    const ok = window.confirm(
      'Reset this device? It will get a new order id and any linked payment or refund (on the device and paired terminal) will be cleared.',
    );
    if (!ok) return;
    await performDeviceReset({ variables: { id } });
    await refetch();
  };

  const handleShowPairingCode = async () => {
    if (pairing) return;
    const ok = window.confirm(
      'Generate a new pairing code? The device must be re-paired and any existing credential will stop working.',
    );
    if (!ok) return;
    const result = await regeneratePairingCode({ variables: { deviceId: id } });
    const pairingData = result.data?.regenerateDevicePairingCode;
    if (!pairingData) return;
    setPairingResult({
      deviceName: pairingData.device.name,
      pairingCode: pairingData.pairingCode,
      expiresAt: pairingData.expiresAt,
    });
    setPairingModalOpen(true);
  };

  if (loading && !data) {
    return <p className="text-sm text-gray-500">Loading device…</p>;
  }

  if (!device) {
    return <p className="text-sm text-gray-500">Device not found.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Edit {device.name}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Rename, categorise, and pair this POS device with a payment terminal.
        </p>
      </div>

      <FormLayout
        title="Device"
        onSubmit={handleSubmit}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => navigate(backHref)}
            >
              Cancel
            </Button>
            <Button id="admin.devices.save" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save device'}
            </Button>
          </div>
        }
      >
        <FormSection title="Identity">
          <FormField label="Name" required>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </FormField>
        </FormSection>
        <FormSection
          title="Assignment"
          description="Pair the device with a category (for charting) and optionally a terminal."
        >
          <FormField label="Category">
            <Select
              id="admin.devices.category"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              options={[
                { value: '', label: 'No category' },
                ...(data?.categories.map((c) => ({
                  value: c.id,
                  label: c.name,
                })) ?? []),
              ]}
            />
          </FormField>
          <FormField label="Allow category switch">
            <Switch
              checked={allowCategorySwitch}
              onChange={(event) => setAllowCategorySwitch(event.target.checked)}
              label={
                allowCategorySwitch
                  ? 'Staff can switch categories and search all products'
                  : 'Register locked to the default category (no search)'
              }
            />
          </FormField>
          <FormField label="Terminal">
            <Select
              id="admin.devices.terminal"
              value={terminalId}
              onChange={(event) => setTerminalId(event.target.value)}
              options={[
                { value: '', label: 'No terminal' },
                ...(data?.terminals.nodes.map((t) => ({
                  value: t.id,
                  label: t.name,
                })) ?? []),
              ]}
            />
          </FormField>
        </FormSection>
        <FormSection title="Status">
          <FormField label="Active">
            <Switch
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
              label={isActive ? 'Accepting transactions' : 'Disabled'}
            />
          </FormField>
        </FormSection>
        <FormSection
          title="ACL role"
          description="Device permissions come from a single assigned role (e.g. iPad Checkout)."
        >
          <FormField label="Role">
            <Select
              id="admin.devices.role"
              value={roleId}
              onChange={(event) => setRoleId(event.target.value)}
              options={[
                { value: '', label: 'No role' },
                ...(rolesCatalog?.roles ?? []).map((role) => ({
                  value: role.id,
                  label: role.name,
                })),
              ]}
            />
          </FormField>
        </FormSection>
        <FormSection
          title="Loyalty"
          description="When on, a PIN payment wraps in an Adyen session and, after success, shows a thank-you (linked card) or a registration QR (unlinked card) on the PIN pad."
        >
          <FormField label="Ask for loyalty">
            <Switch
              id="admin.devices.askLoyalty"
              checked={askLoyalty}
              onChange={(event) => setAskLoyalty(event.target.checked)}
              label={askLoyalty ? 'Loyalty prompt on' : 'Loyalty prompt off'}
            />
          </FormField>
        </FormSection>
        <FormSection
          title="Tipping"
          description="When enabled, PIN payments on this device can prompt the guest for a tip on the terminal."
        >
          <FormField label="Ask for tip">
            <Switch
              id="admin.devices.askTip"
              checked={gratuity.enabled}
              onChange={(event) =>
                setGratuity((prev) => ({ ...prev, enabled: event.target.checked }))
              }
              label={gratuity.enabled ? 'Tip prompt on' : 'Tip prompt off'}
            />
          </FormField>
          {gratuity.enabled && (
            <>
              <FormField label="Minimum amount (€)" description="Order total before the terminal asks for a tip.">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={minimumEuros}
                  onChange={(event) => setMinimumEuros(event.target.value)}
                />
              </FormField>
              <FormField label="Tip % 1">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={gratuity.tipPercent1}
                  onChange={(event) =>
                    setGratuity((prev) => ({
                      ...prev,
                      tipPercent1: parseFloat(event.target.value) || 0,
                    }))
                  }
                />
              </FormField>
              <FormField label="Tip % 2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={gratuity.tipPercent2}
                  onChange={(event) =>
                    setGratuity((prev) => ({
                      ...prev,
                      tipPercent2: parseFloat(event.target.value) || 0,
                    }))
                  }
                />
              </FormField>
              <FormField label="Tip % 3">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={gratuity.tipPercent3}
                  onChange={(event) =>
                    setGratuity((prev) => ({
                      ...prev,
                      tipPercent3: parseFloat(event.target.value) || 0,
                    }))
                  }
                />
              </FormField>
              <FormField label="Round to €0.50">
                <Switch
                  checked={gratuity.roundToHalf}
                  onChange={(event) =>
                    setGratuity((prev) => ({ ...prev, roundToHalf: event.target.checked }))
                  }
                  label={gratuity.roundToHalf ? 'Rounded presets' : 'Exact cents'}
                />
              </FormField>
              <FormField label="Allow custom tip">
                <Switch
                  checked={gratuity.allowCustomTip}
                  onChange={(event) =>
                    setGratuity((prev) => ({ ...prev, allowCustomTip: event.target.checked }))
                  }
                  label={gratuity.allowCustomTip ? 'Guest can enter custom amount' : 'Presets only'}
                />
              </FormField>
            </>
          )}
        </FormSection>
      </FormLayout>

      <Can permission="device:pair">
        <div className="rounded-2xl border border-brand-200/80 bg-brand-50/50 p-6 dark:border-brand-900/50 dark:bg-brand-950/30">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Pair device
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Generate an 8-character code and enter it on the Ferry POS app. Use this
            for a new device or when an existing one lost its credential.
          </p>
          <div className="mt-4">
            <Button
              id="admin.devices.showPairing"
              type="button"
              variant="secondary"
              disabled={pairing}
              onClick={() => void handleShowPairingCode()}
            >
              {pairing ? 'Generating…' : 'Show pairing code'}
            </Button>
          </div>
        </div>
      </Can>

      <div className="rounded-2xl border border-red-200/80 bg-red-50/50 p-6 dark:border-red-900/50 dark:bg-red-950/30">
        <h2 className="text-lg font-semibold text-red-900 dark:text-red-200">
          Reset device
        </h2>
        <p className="mt-1 text-sm text-red-800/90 dark:text-red-200/80">
          Clears linked payment and refund on the device and its paired terminal (if any),
          generates a new order id, and bumps the device version. Use when a device is stuck
          after a failed or abandoned checkout.
        </p>
        <div className="mt-4">
          <Button
            type="button"
            variant="danger"
            disabled={resetting}
            onClick={() => void handleResetDevice()}
          >
            {resetting ? 'Resetting…' : 'Reset device'}
          </Button>
        </div>
      </div>

      <DevicePairingModal
        open={pairingModalOpen}
        pairing={pairingResult}
        onClose={() => setPairingModalOpen(false)}
      />
    </div>
  );
}
